/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * 业务页面中 配合宽表组件 PerformanceMetricsTable使用的逻辑
 */
import React, { useMemo, useState } from "react";
import { useIntl, getLocale } from "umi";
import moment from "moment";
import MetricAndUpDown from "@/pages/dashboard/components/MetricAndUpDown";
import {
  formatAccumulation, formatFilterValues, formatPercentNum, formatNum, commonValueFormatter,
  generateCopyData, getUpAndDownStatus
} from '@/pages/dashboard/utils/utils';
import { TableParamsType, IColumsType, IMetricsInfoType } from "./types";
import { PagingQuery, SortBy } from "@/pages/dashboard/components/PerformanceMetricsTable";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { LANGUAGE_EN, TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from "@/pages/home/_config/config";
import { IExplain } from "@/pages/dashboard/types/dashboard";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { dynamicString } from "@home/utils/dynamicString";
import { getCustomFilterList } from "@/pages/dashboard/components/NewFilter/customFilterList";

export function useTableState() {
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // 排序
  const [sort, setSort] = useState<SortBy[]>([]);
  // 总条数
  const [total, setTotal] = useState(0);

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);
  return {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  };
}

// 表格上传参数
export function getTableRequestParams({ gameCode, filterValue: formVal, accumulated, sort, paging, singleDaily, selectType, ...resetParams }: TableParamsType) {
  const { by, order } = sort?.[0] || { by: 'date', order: 'desc' };
  const { timeType, diff, ...filters } = formVal || {};
  const rawSortedBy = by?.replace(selectType, '');
  const sortedBy = rawSortedBy === 'time' ? 'date' : rawSortedBy;
  // const [start, end] = rangeTime;
  const params = {
    game_code: gameCode,
    filters: formatFilterValues(filters),
    accumulation: formatAccumulation(accumulated),
    page: paging.pageIndex,
    page_size: paging.pageSize,
    // start_date: start,
    // end_date: end,
    sorted_by: sortedBy,
    desc: order === 'desc',
    date: singleDaily,
    ...resetParams
  };
  return params;
}

export function useTableColumns() {
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  // 日期列, 周六日的颜色高亮,
  const DATE_COLUMN = {
    key: 'time',
    copyKey: 'time',
    header: formatMessage({ id: "common_time" }),
    width: 155,
    fixed: "left",
    align: "left",
    render(record: { date: string; }) {
      // 将日期字符串解析为moment对象
      const dateStr = record?.date;
      const datetime = record?.date.split(" ");
      const time = datetime[1];
      const hm = time.split(":").slice(0, 2).join(':');
  
      let formattedTime = '';
      if (hm === '24:00') {
        formattedTime = '24:00';
      } else {
        const momentDate = moment(dateStr);
        formattedTime = momentDate.format('HH:mm');
      }
      return <div className='dbd-overiew-table-date-columns-box'>
        <span className='table-text'>{formattedTime}</span>
      </div>;
    }
  };

  // Accumulate 列
  const getAccumulateColumns = (accumulates: string[] = []) => {
    // if (accumulates[0] === ACCUMULATED_VALUE) return [];
    const columns: any[] = [];
    accumulates.forEach((value: string, i) => {
      // @ts-ignore
      if (i > 0) {
        return columns.push({
          key: value,
          copyKey: value,
          header: formatMessage({ id: `common_${value}` }),
          align: 'left',
          width: 200,
          render(record: { dimension_name: { [x: string]: any; }; }) {
            const customFilters = getCustomFilterList();
            if (customFilters?.includes(value)) {
              const name = record?.custom_dimensions?.find(item => item?.key === value)?.value || '-';
              return name;
            }
            return record.dimension_name?.[`${value}_name_${isEn ? 'en' : 'cn'}`] || "-";
          }
        });
      }
    });

    return columns;
  };

  // 指标列
  // 通常情况下metricsKey就是metricsInfo对象的key, 但是国家对比页面的metricsKey是受控的
  const getMetricColumns = (metricsKey: string[], metricsInfo: { [x: string]: any; }, explainInfo: IExplain, selectType: string) => {
    const isShowUpDown = selectType !== "value_only";
    return metricsKey.reduce((columns: IColumsType[], key: string) => {
      const info = metricsInfo[key];
      const { metricsConfig } = info || {};
      const { type, rainbowKey, explainEn,explainCn,enPercentWidth, cnPercentWidth, enWidth, cnWidth, enName, cnName } = metricsConfig || {};
      const width = (isEn ? enPercentWidth : cnPercentWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      const onlyValueWidth = (isEn ? enWidth : cnWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      const name = isEn ? enName : cnName;
      const isPercent = type === 'percent';
      const explain = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
      // @ts-ignore
      columns.push(
        {
          key: key + selectType,
          copyKey: key,
          selectType,
          header: name,
          align: "right",
          explain,
          width: isShowUpDown ? width : onlyValueWidth,
          isPercent,
          render(record) {
            const metric = record?.metrics?.[key] || {};
            const { progressWidth, metricValue } = metric;

            return (
              <>
                <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                {/* <div className={'table-text-bg'} style={{width: `${progressWidth}%`}} /> */}
                <MetricAndUpDown
                  metric={metricValue}
                  updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                  hasTooltip={false}
                />
              </>);
          }
        },
      );
      return columns;
    }, []);
  };

  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns,
  };
}

/**
 * 复制当前列，语言切换，对应当前语言的取值
 * @param data 当前行数据
 */
const getCopyRenderValues = (data: any, extendConfig?: { gameCode?: any; selector?: any; }) => {
  const isEn = getLocale() === LANGUAGE_EN;
  if (!extendConfig) {
    const { dimension_name } = data || {};
    if (dimension_name) {
      Object.keys(data).forEach(key => {
        const value = data[key];
        data[key] = dimension_name?.[`${key}_${isEn ? 'en' : 'cn'}`] || value;
      });
    }
  } else {
    data = generateCopyData(data, { gameCode: extendConfig?.gameCode, selectorList: extendConfig.selector });
  }
  return data;
};

// 表格返回数据格式整理
export function useTableDataFormat(tableData: any[], metricsInfo: IMetricsInfoType, timeType = "Day", isCopyData?: boolean, extendConfig?: { gameCode?: any; selector?: any; }) {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    const WEEK_DAY_ARR = [
      formatMessage({ id: 'common_mon' }),
      formatMessage({ id: 'common_tues' }),
      formatMessage({ id: 'common_wed' }),
      formatMessage({ id: 'common_thur' }),
      formatMessage({ id: 'common_fri' }),
      formatMessage({ id: 'common_sat' }),
      formatMessage({ id: 'common_sun' }),
    ];
    return tableData.map((data) => {
      if (isCopyData) {
        data = getCopyRenderValues(data, extendConfig);
      }
      const { date, metrics, type, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      // const dateStr = timeType === "Month" ? moment(date).format("YYYY-MM") : moment(date).format("YYYY-MM-DD") + getWeekDay(date, WEEK_DAY_ARR);
  

      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        // if (!metricsInfo[key]) return; // 后端会返回全量指标的数据, 而前端页面可能只展示部分指标
        const { value, dod, sdlw } = metrics[key] || { value: "", dod: "", sdlw: "" };
        const { metricsConfig } = metricsInfo[key] || {};
        const { type, decimal, percentDecimals, formatter } = metricsConfig;
        const isPercent = type === 'percent';
        const progressWidth = isPercent ? value * 100 : 0; // 单元格内进度条的宽度
        // const metricValue = isPercent ? formatPercentNum(value, decimals, false) : formatNum(value, false, isUserType);
        const metricValue = formatter ? dataFormat({ value, formatRule: formatter }) : commonValueFormatter(type, value, decimal) || value;
        const dodStatus = getUpAndDownStatus(dod, false);
        const sdlwStatus = getUpAndDownStatus(sdlw, false);
        const dodPercentStr = formatPercentNum(dod, percentDecimals, true);
        const sdlwPercentStr = formatPercentNum(sdlw, percentDecimals, true);

        metrics[key] = {
          // progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          // dodStatus,
          // sdlwStatus,
          // dodPercentStr,
          // sdlwPercentStr,
          ...metrics[key]
        };
      });

      return {
        date,
        metrics,
        ...accumulates
      };
    });
    // if (customFormat) return customFormat(newData); // 为了配合外部的自定义格式整理
    // return newData;
  }, [tableData, getLocale()]);
}
// 表格返回统计值数据格式整理 (和tableData用同一个hook时, 会因为useMemo调用次数太多而崩溃)
export function useStatisticsDataFormat(statisticsData: any[], metricsInfo: IMetricsInfoType, showStatistic: any[], isCopyData: boolean) {
  const { formatMessage } = useIntl();

  const statisticsObj = useMemo(() => {
    if (!Array.isArray(statisticsData)) return {};
    return statisticsData.reduce((acc, data) => {
      const { date, metrics, type: statisticsType, ...accumulates } = data || {};
      if (!metrics) return acc;
      // 日期列数据 (如果type有值， 说明是聚合数据)
      const dateStr = formatMessage({ id: `common_${statisticsType}` }); // 统计值行的数据, 日期列要展示对应的聚合标题
      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        const { value, dod } = metrics[key] || { value: "", dod: "" };
        const { metricsConfig } = metricsInfo[key] || {};
        const { type, decimal, percentDecimals } = metricsConfig;
        const isPercent = type === 'percent';
        // const { isPercent, decimals, isUserType } = metricsInfo[key] // TODO 如果指标信息的配置对象格式不同, 导致逻辑无法复用
        const progressWidth = isPercent && statisticsType !== 'count' ? value * 100 : 0; // 单元格内进度条的宽度
        // const metricValue = isCopyData ? value : isPercent && type !== 'count' ? formatPercentNum(value, decimals, false) : formatNum(value, false, type == 'count' ? true : isUserType);
        const metricValue = isCopyData ? value : statisticsType === 'count' ? formatNum(value, false, true) : commonValueFormatter(type, value, decimal);
        metrics[key] = {
          // progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          // dodStatus: "no",
          // sdlwStatus: "no",
          // dodPercentStr: "",
          // sdlwPercentStr: "",
          ...metrics[key]
        };
      });

      acc[statisticsType] = {
        date,
        metrics,
        ...accumulates
      };
      return acc;
    }, {});
  }, [statisticsData, getLocale()]);
  return Object.keys(statisticsObj).length > 0 ? showStatistic.map(type => statisticsObj[type]) : [];
}

/**
 * 处理在 Day, Week , Month不同维度下, 显示的日期格式
 * @param dateDimension 日期维度
 * @param date 日期
 * @return 返回处理后符合产品要求的日期格式字符串
 */
export const getDateFormatString = (dateDimension: string, date: string) => {
  switch (dateDimension) {
    case "Week": {
      const weekOfDay = moment(date).format('E');
      return moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD");
    }
    case "Month": {
      return moment(date).format("YYYY-MM");
    }
    case "Day":
    default:
      return moment(date).format("YYYY-MM-DD");
  }
};

export const useMetricsColumns = (configData?: any, data = {}) => {
  const getColumn = (configData: any) => {
    const columnObj: any = {};
    configData.forEach((item: any, index: any) => {
      const { metricKey } = item || {};
      columnObj[metricKey] = {
        index,
        metricsConfig: {
          ...item,
          enName: dynamicString(item?.enName, data),
          cnName: dynamicString(item?.cnName, data),
          explainEn: dynamicString(item?.explainEn, data),
          explainCn: dynamicString(item?.explainCn, data),
          formatter: dynamicString(item?.formatter, data),
        },
      };
    });
    return columnObj;
  };
  // 如果后端获取不到会拿前端的配置，所以这里不用写默认columns
  return getColumn(configData);
};


